// src/components/product/ProductsClient.tsx
"use client";

import { useMemo, useState, useTransition } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import Container from "@/components/layout/Container";
import FiltersSidebar, {
  FiltersState,
  Facets,
} from "@/components/search/FiltersSidebar";
import type { Product } from "@/types/product";
import Link from "next/link";
import SmartImg from "@/components/product/SmartImg";

export default function ProductsClient({
  initialItems,
  initialTotal,
  initialFilters,
  facets,
}: {
  initialItems: Product[];
  initialTotal: number;
  initialFilters: FiltersState;
  facets: Facets;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const sp = useSearchParams();

  const [filters, setFilters] = useState<FiltersState>(initialFilters);
  const [isPending, startTransition] = useTransition();

  const baseParams = useMemo(() => new URLSearchParams(sp.toString()), [sp]);

  function buildUrl(next: FiltersState) {
    const params = new URLSearchParams(baseParams.toString());

    params.delete("q");
    params.delete("min");
    params.delete("max");
    params.delete("brand");
    params.delete("cond");

    if (next.q.trim()) params.set("q", next.q.trim());
    if (next.min.trim()) params.set("min", next.min.trim());
    if (next.max.trim()) params.set("max", next.max.trim());
    if (next.brands.length) params.set("brand", next.brands.join(","));
    if (next.conditions.length) params.set("cond", next.conditions.join(","));

    const qs = params.toString();
    return qs ? `${pathname}?${qs}` : pathname;
  }

  function applyToUrl(next: FiltersState) {
    const url = buildUrl(next);
    startTransition(() => {
      router.push(url);
      // ✅ Do NOT router.refresh(); it often adds extra latency/jank.
    });
  }

  function onReset() {
    const cleared: FiltersState = {
      q: "",
      min: "",
      max: "",
      brands: [],
      conditions: [],
    };
    setFilters(cleared);
    applyToUrl(cleared);
  }

  return (
    <Container>
      <div className="py-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-100">
              Products
            </h1>
            <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">
              Browse all items currently in the warehouse catalog.
            </p>
          </div>

          {isPending ? (
            <div className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
              Updating…
            </div>
          ) : null}
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-[260px_1fr]">
          <FiltersSidebar
            value={filters}
            facets={facets}
            onChange={setFilters}
            onApply={() => applyToUrl(filters)}
            onReset={onReset}
          />

          <section className="rounded-xl border border-neutral-200 bg-white p-4 shadow-sm dark:border-neutral-800 dark:bg-neutral-900">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                Results
              </div>
              <div className="text-xs text-neutral-500 dark:text-neutral-400">
                {initialTotal} item(s)
              </div>
            </div>

            <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {initialItems.map((p) => {
                const img = p.images?.[0]?.url ?? "";
                return (
                  <Link
                    key={p.id}
                    href={`/products/${p.slug}`}
                    className="group overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-sm transition hover:shadow-md dark:border-neutral-800 dark:bg-neutral-950"
                    prefetch
                  >
                    <div className="aspect-[4/5] w-full bg-neutral-100 dark:bg-neutral-800">
                      {img ? (
                        <SmartImg
                          src={img}
                          alt={p.name}
                          className="h-full w-full object-cover"
                          loading="lazy"
                        />
                      ) : null}
                    </div>

                    <div className="p-3">
                      <div className="line-clamp-2 text-sm font-medium text-neutral-900 dark:text-neutral-100">
                        {p.name}
                      </div>
                      <div className="mt-2 text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                        ${p.price.toFixed(2)}
                      </div>
                      <div className="mt-2 flex flex-wrap gap-2 text-xs text-neutral-600 dark:text-neutral-400">
                        {p.condition ? (
                          <span className="rounded-full border border-neutral-200 px-2 py-0.5 dark:border-neutral-700">
                            {p.condition}
                          </span>
                        ) : null}
                        {p.brand ? (
                          <span className="rounded-full border border-neutral-200 px-2 py-0.5 dark:border-neutral-700">
                            {p.brand}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>

            {initialItems.length === 0 ? (
              <div className="mt-6 rounded-lg border border-neutral-200 bg-neutral-50 p-4 text-sm text-neutral-700 dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-300">
                No results found.
              </div>
            ) : null}
          </section>
        </div>
      </div>
    </Container>
  );
}
